import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type Ref,
} from 'react';
import { Icon } from '../../components/Icon';
import { useI18n } from '../../i18n/i18n-context';
import { cx } from '../../lib/cx';
import { useTrack } from '../../lib/tracking-context';
import './promo-overview.css';

const clock = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;

/** The video by its file name, as the insights count plays of it. */
const videoName = (url: string): string =>
  (url.split('/').at(-1) ?? '')
    .replace(/\.[^.]*$/, '')
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .slice(0, 48) || 'video';

const rangeStyle = (fraction: number): CSSProperties =>
  ({ '--range-progress': `${fraction * 100}%` }) as CSSProperties;

function setCaptionMode(track: TextTrack, showing: boolean) {
  track.mode = showing ? 'showing' : 'hidden';
}

/** What a page can do with a player it shows. */
export interface PromoPlayerHandle {
  /** The film, to follow as it plays. */
  readonly video: HTMLVideoElement | null;
  /** Plays from `time`, in seconds, whether it was playing or not. */
  readonly playFrom: (time: number) => void;
}

/** The overview's branded controls; the media element owns playback state. */
export function PromoPlayer({
  ref,
  src,
  poster,
  label,
  watchLabel,
  initialDuration = 60,
  preload = 'metadata',
  sources,
  captions,
}: {
  readonly ref?: Ref<PromoPlayerHandle>;
  readonly src: string;
  readonly poster: string;
  readonly label?: string;
  readonly watchLabel?: string;
  readonly initialDuration?: number;
  readonly preload?: 'none' | 'metadata';
  readonly sources?: readonly { readonly src: string; readonly type: string }[];
  /** English narration captions, when available. */
  readonly captions?: string;
}) {
  const { t } = useI18n();
  const accessibleLabel = label ?? t.promo.label;
  const startLabel = watchLabel ?? t.promo.watch;
  const playerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [ended, setEnded] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(initialDuration);
  const [loaded, setLoaded] = useState(false);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [fullWindow, setFullWindow] = useState(false);
  const [failed, setFailed] = useState(false);
  const [showCaptions, setShowCaptions] = useState(false);
  const silent = muted || volume === 0;
  const track = useTrack();
  // Counted once each: the first play, and the first time it's watched to the end.
  const counted = useRef({ started: false, finished: false });
  const countPlay = (stage: 'started' | 'finished') => {
    if (counted.current[stage]) return;
    counted.current[stage] = true;
    track({ event: 'video', video: videoName(src), stage });
  };

  useEffect(() => {
    const video = videoRef.current;
    const player = playerRef.current;
    const syncFullScreen = () => {
      setFullScreen(document.fullscreenElement === player);
    };
    document.addEventListener('fullscreenchange', syncFullScreen);
    return () => {
      // Leaving the page also stops its soundtrack.
      video?.pause();
      document.removeEventListener('fullscreenchange', syncFullScreen);
    };
  }, []);

  useEffect(() => {
    if (!fullWindow) return;
    const player = playerRef.current;
    if (!player) return;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    player.focus({ preventScroll: true });
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setFullWindow(false);
      } else if (event.key === 'Tab') {
        const controls = [...player.querySelectorAll<HTMLElement>('button, input')].filter(
          (control) => !control.hasAttribute('disabled') && control.getClientRects().length > 0,
        );
        const first = controls[0];
        const last = controls.at(-1);
        if (
          event.shiftKey &&
          (document.activeElement === first || document.activeElement === player)
        ) {
          event.preventDefault();
          last?.focus();
        } else if (
          !event.shiftKey &&
          (document.activeElement === last || document.activeElement === player)
        ) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKey, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKey, true);
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) {
        previousFocus.focus({ preventScroll: true });
      }
    };
  }, [fullWindow]);

  async function play(video: HTMLVideoElement) {
    try {
      await video.play();
    } catch (error) {
      // A close or a quick pause can cancel the pending play request.
      if (video.isConnected && !(error instanceof DOMException && error.name === 'AbortError')) {
        setFailed(true);
        setBuffering(false);
      }
    }
  }

  async function togglePlayback() {
    const video = videoRef.current;
    if (!video || failed) return;
    if (!video.paused) {
      video.pause();
      return;
    }
    if (ended || video.ended || video.currentTime >= duration) {
      video.currentTime = 0;
      setCurrentTime(0);
    }
    await play(video);
  }

  useImperativeHandle(ref, () => ({
    get video() {
      return videoRef.current;
    },
    playFrom(time: number) {
      const video = videoRef.current;
      if (!video || failed) return;
      // Before the film's length is known, the browser starts from here once it can.
      video.currentTime = Math.max(0, time);
      setCurrentTime(video.currentTime);
      setEnded(false);
      if (video.paused) void play(video);
    },
  }));

  function seek(time: number) {
    const video = videoRef.current;
    if (!video || !loaded) return;
    video.currentTime = Math.max(0, Math.min(duration, time));
    setCurrentTime(video.currentTime);
    setEnded(video.currentTime >= duration);
  }

  function toggleMute() {
    const video = videoRef.current;
    if (!video) return;
    if (silent) {
      video.muted = false;
      if (video.volume === 0) video.volume = 1;
    } else {
      video.muted = true;
    }
  }

  async function toggleFullScreen() {
    const player = playerRef.current;
    if (!player) return;
    if (fullWindow) {
      setFullWindow(false);
      return;
    }
    try {
      if (document.fullscreenElement === player) await document.exitFullscreen();
      else if (document.fullscreenEnabled) await player.requestFullscreen();
      else setFullWindow(true);
    } catch {
      // Keep the large view available when native fullscreen is unavailable or declined.
      setFullWindow(true);
    }
  }

  const playbackLabel = playing ? t.promo.pause : ended ? t.promo.replay : t.promo.play;

  return (
    <>
      <div
        ref={playerRef}
        className={cx('promo-player', fullWindow && 'promo-player--full-window')}
        role={fullWindow ? 'dialog' : undefined}
        aria-modal={fullWindow || undefined}
        aria-label={fullWindow ? accessibleLabel : undefined}
        tabIndex={fullWindow ? -1 : undefined}
      >
        <div className="promo-player-screen" aria-busy={buffering}>
          <video
            ref={videoRef}
            className="promo-overview-video"
            src={sources?.length ? undefined : src}
            poster={poster}
            aria-label={accessibleLabel}
            playsInline
            preload={preload}
            onLoadedMetadata={(event) => {
              const length = event.currentTarget.duration;
              if (Number.isFinite(length) && length > 0) {
                setDuration(length);
                setLoaded(true);
              }
            }}
            onTimeUpdate={(event) => {
              setCurrentTime(event.currentTarget.currentTime);
            }}
            onPlay={(event) => {
              countPlay('started');
              setPlaying(true);
              setEnded(false);
              setBuffering(event.currentTarget.readyState < 3);
            }}
            onPlaying={() => {
              setBuffering(false);
            }}
            onWaiting={(event) => {
              setBuffering(!event.currentTarget.paused);
            }}
            onCanPlay={() => {
              setBuffering(false);
            }}
            onPause={() => {
              setPlaying(false);
              setBuffering(false);
            }}
            onEnded={() => {
              countPlay('finished');
              setEnded(true);
              setPlaying(false);
              setBuffering(false);
            }}
            onVolumeChange={(event) => {
              setVolume(event.currentTarget.volume);
              setMuted(event.currentTarget.muted);
            }}
            onError={() => {
              setFailed(true);
              setPlaying(false);
              setBuffering(false);
            }}
          >
            {sources?.map((source) => (
              <source key={source.src} src={source.src} type={source.type} />
            ))}
            {captions && <track kind="captions" src={captions} srcLang="en" label="English" />}
          </video>
          <button
            type="button"
            className={cx('promo-player-overlay', !playing && 'promo-player-overlay--paused')}
            aria-label={playing ? t.promo.pause : ended ? t.promo.replay : startLabel}
            disabled={failed}
            onClick={() => void togglePlayback()}
          >
            {(!playing || buffering) && !failed && (
              <span className="promo-player-start">
                <span className="promo-player-play-mark">
                  {buffering ? (
                    <span className="promo-player-spinner" />
                  ) : (
                    <Icon name={ended ? 'retry' : 'play'} />
                  )}
                </span>
                <span className="promo-player-play-label">
                  {buffering ? t.promo.loading : ended ? t.promo.replay : startLabel}
                </span>
              </span>
            )}
          </button>
        </div>
        <div
          className={cx('promo-player-controls', captions && 'promo-player-controls--captioned')}
          role="group"
          aria-label={accessibleLabel}
        >
          <button
            type="button"
            className="promo-player-button promo-player-play"
            aria-label={playbackLabel}
            title={playbackLabel}
            disabled={failed}
            onClick={() => void togglePlayback()}
          >
            <Icon name={playing ? 'pause' : ended ? 'retry' : 'play'} />
          </button>
          <input
            type="range"
            className="promo-player-range promo-player-seek"
            aria-label={t.promo.seek}
            aria-valuetext={`${clock(currentTime)} / ${clock(duration)}`}
            dir="ltr"
            min={0}
            max={duration}
            step={0.1}
            value={currentTime}
            disabled={!loaded || failed}
            style={rangeStyle(currentTime / duration)}
            onChange={(event) => {
              seek(event.currentTarget.valueAsNumber);
            }}
            onKeyDown={(event) => {
              if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
              event.preventDefault();
              seek(currentTime + (event.key === 'ArrowLeft' ? -5 : 5));
            }}
          />
          <span className="promo-player-time" dir="ltr">
            {clock(currentTime)} <span>/ {clock(duration)}</span>
          </span>
          <div className="promo-player-audio">
            <button
              type="button"
              className="promo-player-button"
              aria-label={silent ? t.promo.unmute : t.promo.mute}
              title={silent ? t.promo.unmute : t.promo.mute}
              onClick={toggleMute}
            >
              <Icon name={silent ? 'muted' : 'speaker'} />
            </button>
            <input
              type="range"
              className="promo-player-range promo-player-volume"
              aria-label={t.promo.volume}
              dir="ltr"
              min={0}
              max={1}
              step={0.05}
              value={silent ? 0 : volume}
              style={rangeStyle(silent ? 0 : volume)}
              onChange={(event) => {
                const video = videoRef.current;
                if (!video) return;
                video.volume = event.currentTarget.valueAsNumber;
                video.muted = false;
              }}
            />
          </div>
          {captions && (
            <button
              type="button"
              className="promo-player-button promo-player-captions"
              aria-label={t.promo.captions}
              aria-pressed={showCaptions}
              title={t.promo.captions}
              onClick={() => {
                const track = videoRef.current?.textTracks[0];
                if (!track) return;
                setCaptionMode(track, !showCaptions);
                setShowCaptions(!showCaptions);
              }}
            >
              <span aria-hidden="true">CC</span>
            </button>
          )}
          <button
            type="button"
            className="promo-player-button promo-player-fullscreen"
            aria-label={fullScreen || fullWindow ? t.editor.collapse : t.editor.expand}
            title={fullScreen || fullWindow ? t.editor.collapse : t.editor.expand}
            onClick={() => void toggleFullScreen()}
          >
            <Icon name={fullScreen || fullWindow ? 'collapse' : 'expand'} />
          </button>
        </div>
      </div>
      {failed && (
        <p className="promo-overview-error" role="status">
          {t.promo.failed}{' '}
          <a href={src} target="_blank" rel="noreferrer">
            {t.promo.openVideo}
          </a>
        </p>
      )}
    </>
  );
}
